import { DutyRule } from "@/services/product-catalogue/duty-columns-api";

// This represents the delta payload for updating a rule.
// It's designed to match the backend's delta-update API.
export interface DutyRuleDelta {
  _id: string;
  priority?: number;
  rate?: number;
  startDate?: string;
  endDate?: string | null;
  authority?: string;
  rateType?: string;
  valueBasis?: string;
  isMetal?: boolean;
  exclusiveGroup?: string;
  exclusiveMode?: string;
  addHtsCodes?: string[];
  removeHtsCodes?: string[];
  addExclusions?: string[];
  removeExclusions?: string[];
  addCountries?: string[];
  removeCountries?: string[];
  addCountryExclusions?: string[];
  removeCountryExclusions?: string[];
  addAllowedStacksWith?: string[];
  removeAllowedStacksWith?: string[];
  addNonMetalAllowedStacksWith?: string[];
  removeNonMetalAllowedStacksWith?: string[];
  addRequiredWith?: string[];
  removeRequiredWith?: string[];
}

/**
 * Compares two arrays and returns the items added and removed.
 * @param original - The original array.
 * @param updated - The updated array.
 * @returns An object with `added` and `removed` arrays.
 */
const getArrayDiff = <T>(original: T[] = [], updated: T[] = []) => {
  const originalSet = new Set(original);
  const updatedSet = new Set(updated);

  const added = [...updatedSet].filter(item => !originalSet.has(item));
  const removed = [...originalSet].filter(item => !updatedSet.has(item));

  return { added, removed };
};

/**
 * Calculates the delta between an original and an updated duty rule.
 * @param originalRule - The pristine rule object from the server.
 * @param updatedRule - The rule object with user's modifications.
 * @returns A delta object containing only the changed fields.
 */
export const calculateRuleDelta = (originalRule: DutyRule, updatedRule: DutyRule): DutyRuleDelta => {
  const delta: DutyRuleDelta = { _id: originalRule._id };

  // Scalar fields
  const scalarFields: (keyof DutyRule)[] = [
    'priority', 'startDate', 'endDate', 'authority', 'rateType', 
    'valueBasis', 'isMetal', 'exclusiveGroup', 'exclusiveMode',
    'ceilingPercent', 'ceilingAgainstAuthority'
  ];

  for (const field of scalarFields) {
    if (originalRule[field] !== updatedRule[field]) {
      // @ts-ignore
      delta[field] = updatedRule[field];
    }
  }

  // Rate is a special case (string in form, number in rule)
  // The updatedRule passed in should have rate converted to a number already.
  if (originalRule.rate !== updatedRule.rate) {
    delta.rate = updatedRule.rate;
  }

  // Array fields
  const arrayDiffs = {
    htsCodes: getArrayDiff(originalRule.htsCodes, updatedRule.htsCodes),
    exclusions: getArrayDiff(originalRule.exclusions, updatedRule.exclusions),
    countries: getArrayDiff(originalRule.countries, updatedRule.countries),
    countryExclusions: getArrayDiff(originalRule.countryExclusions, updatedRule.countryExclusions),
    allowedStacksWith: getArrayDiff(originalRule.allowedStacksWith, updatedRule.allowedStacksWith),
    nonMetalAllowedStacksWith: getArrayDiff(originalRule.nonMetalAllowedStacksWith, updatedRule.nonMetalAllowedStacksWith),
    requiredWith: getArrayDiff(originalRule.requiredWith, updatedRule.requiredWith),
  };

  if (arrayDiffs.htsCodes.added.length > 0) delta.addHtsCodes = arrayDiffs.htsCodes.added;
  if (arrayDiffs.htsCodes.removed.length > 0) delta.removeHtsCodes = arrayDiffs.htsCodes.removed;

  if (arrayDiffs.exclusions.added.length > 0) delta.addExclusions = arrayDiffs.exclusions.added;
  if (arrayDiffs.exclusions.removed.length > 0) delta.removeExclusions = arrayDiffs.exclusions.removed;

  if (arrayDiffs.countries.added.length > 0) delta.addCountries = arrayDiffs.countries.added;
  if (arrayDiffs.countries.removed.length > 0) delta.removeCountries = arrayDiffs.countries.removed;

  if (arrayDiffs.countryExclusions.added.length > 0) delta.addCountryExclusions = arrayDiffs.countryExclusions.added;
  if (arrayDiffs.countryExclusions.removed.length > 0) delta.removeCountryExclusions = arrayDiffs.countryExclusions.removed;

  if (arrayDiffs.allowedStacksWith.added.length > 0) delta.addAllowedStacksWith = arrayDiffs.allowedStacksWith.added;
  if (arrayDiffs.allowedStacksWith.removed.length > 0) delta.removeAllowedStacksWith = arrayDiffs.allowedStacksWith.removed;
  
  if (arrayDiffs.nonMetalAllowedStacksWith.added.length > 0) delta.addNonMetalAllowedStacksWith = arrayDiffs.nonMetalAllowedStacksWith.added;
  if (arrayDiffs.nonMetalAllowedStacksWith.removed.length > 0) delta.removeNonMetalAllowedStacksWith = arrayDiffs.nonMetalAllowedStacksWith.removed;

  if (arrayDiffs.requiredWith.added.length > 0) delta.addRequiredWith = arrayDiffs.requiredWith.added;
  if (arrayDiffs.requiredWith.removed.length > 0) delta.removeRequiredWith = arrayDiffs.requiredWith.removed;

  return delta;
}; 